import React from 'react';
import { Card, CardBody, CardHeader } from '../../../../components/Atoms/Card';
import Button from '../../../../components/Atoms/Button';
import { useI18n } from '../../../../i18n';

const SAMPLING_MODES = [
  { value: 'fps', labelKey: 'videoToImages.options.samplingFps' },
  { value: 'nth', labelKey: 'videoToImages.options.samplingNth' },
  { value: 'count', labelKey: 'videoToImages.options.samplingCount' },
];

// JPEG only for now; the backend's OUTPUT_FORMATS table is what gates this, so
// adding PNG later is a row there plus an entry here.
const OUTPUT_FORMATS = [{ value: 'jpeg', label: 'JPEG' }];

/**
 * Step 3 — sampling / format / resize. Every value is re-validated server-side
 * in `validate_options`; the constraints here just keep the UI honest.
 */
const ExtractionOptionsForm = ({ options, isStarting, isUploading, canStart, handlers, onStart }) => {
  const { t } = useI18n();
  const busy = isStarting || isUploading;

  const numberField = (key, labelKey, hintKey, extra = {}) => (
    <div className="col-md-6 mb-2">
      <label className="v2i-label" htmlFor={`v2i-${key}`}>{t(labelKey)}</label>
      <input
        id={`v2i-${key}`}
        type="number"
        className="form-control"
        value={options[key]}
        disabled={busy}
        onChange={(event) => handlers.setOption(key, event.target.value === '' ? '' : Number(event.target.value))}
        {...extra}
      />
      <p className="v2i-hint mb-0">{t(hintKey)}</p>
    </div>
  );

  return (
    <Card className="v2i-card">
      <CardHeader
        title={t('videoToImages.options.heading')}
        actions={
          <Button variant="ghost" size="sm" onClick={handlers.resetOptions} disabled={busy}>
            <i className="fa fa-undo mr-1" aria-hidden="true"></i>
            {t('common.retry')}
          </Button>
        }
      />
      <CardBody>
        {/* --- sampling --- */}
        <div className="v2i-field-group">
          <span className="v2i-label">{t('videoToImages.options.sampling')}</span>
          <div className="v2i-segmented" role="radiogroup" aria-label={t('videoToImages.options.sampling')}>
            {SAMPLING_MODES.map((mode) => (
              <button
                key={mode.value}
                type="button"
                role="radio"
                aria-checked={options.samplingMode === mode.value}
                className={`v2i-segmented__item ${options.samplingMode === mode.value ? 'v2i-segmented__item--active' : ''}`}
                disabled={busy}
                onClick={() => handlers.setOption('samplingMode', mode.value)}
              >
                {t(mode.labelKey)}
              </button>
            ))}
          </div>
        </div>

        <div className="row">
          {options.samplingMode === 'fps' && numberField(
            'fps', 'videoToImages.options.fpsLabel', 'videoToImages.options.fpsHint',
            { min: 0.1, max: 120, step: 0.1 }
          )}
          {options.samplingMode === 'nth' && numberField(
            'nthFrame', 'videoToImages.options.nthLabel', 'videoToImages.options.nthHint',
            { min: 1, step: 1 }
          )}
          {options.samplingMode === 'count' && numberField(
            'totalCount', 'videoToImages.options.countLabel', 'videoToImages.options.countHint',
            { min: 1, max: 100000, step: 1 }
          )}

          <div className="col-md-6 mb-2">
            <label className="v2i-label" htmlFor="v2i-format">{t('videoToImages.options.format')}</label>
            <select
              id="v2i-format"
              className="form-control"
              value={options.outputFormat}
              disabled={busy || OUTPUT_FORMATS.length === 1}
              onChange={(event) => handlers.setOption('outputFormat', event.target.value)}
            >
              {OUTPUT_FORMATS.map((format) => (
                <option key={format.value} value={format.value}>{format.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* --- quality --- */}
        <div className="v2i-field-group">
          <label className="v2i-label" htmlFor="v2i-quality">
            {t('videoToImages.options.quality')} — <strong>{options.quality}</strong>
          </label>
          <input
            id="v2i-quality"
            type="range"
            className="v2i-range"
            min={1}
            max={100}
            step={1}
            value={options.quality}
            disabled={busy}
            onChange={(event) => handlers.setOption('quality', Number(event.target.value))}
          />
          <p className="v2i-hint mb-0">{t('videoToImages.options.qualityHint')}</p>
        </div>

        {/* --- resize --- */}
        <div className="v2i-field-group">
          <span className="v2i-label">{t('videoToImages.options.resize')}</span>
          <div className="v2i-segmented" role="radiogroup" aria-label={t('videoToImages.options.resize')}>
            {[
              { value: 'keep', labelKey: 'videoToImages.options.resizeKeep' },
              { value: 'max', labelKey: 'videoToImages.options.resizeMax' },
            ].map((mode) => (
              <button
                key={mode.value}
                type="button"
                role="radio"
                aria-checked={options.resizeMode === mode.value}
                className={`v2i-segmented__item ${options.resizeMode === mode.value ? 'v2i-segmented__item--active' : ''}`}
                disabled={busy}
                onClick={() => handlers.setOption('resizeMode', mode.value)}
              >
                {t(mode.labelKey)}
              </button>
            ))}
          </div>
          {options.resizeMode === 'max' && (
            <div className="row mt-2">
              {numberField('maxWidth', 'videoToImages.options.maxWidth', 'videoToImages.options.resizeHint',
                { min: 16, max: 10000, step: 1 })}
              {numberField('maxHeight', 'videoToImages.options.maxHeight', 'videoToImages.options.resizeHint',
                { min: 16, max: 10000, step: 1 })}
            </div>
          )}
        </div>

        <div className="v2i-start">
          <Button variant="primary" onClick={onStart} disabled={busy || !canStart}>
            {busy
              ? <><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true"></i>{t('videoToImages.start.starting')}</>
              : <><i className="fa fa-play mr-1" aria-hidden="true"></i>{t('videoToImages.start.button')}</>}
          </Button>
          <p className="v2i-hint mb-0">{t('videoToImages.jobs.background')}</p>
        </div>
      </CardBody>
    </Card>
  );
};

export default ExtractionOptionsForm;
